/**
 * @evocha/mock-data — Shared demo data for web-agent and web-admin portals.
 *
 * Consolidated from:
 * - apps/web-agent/src/lib/demo.ts
 * - apps/web-admin/src/pages/Dashboard.tsx
 * - apps/web-admin/src/pages/Orders.tsx
 * - apps/web-admin/src/pages/Agents.tsx
 * - apps/web-admin/src/pages/Wallets.tsx
 * - apps/web-admin/src/pages/KYC.tsx
 * - apps/web-admin/src/pages/Funding.tsx
 * - apps/web-admin/src/pages/Approvals.tsx
 * - apps/web-admin/src/pages/Finance.tsx
 * - apps/web-admin/src/pages/Refunds.tsx
 * - apps/web-admin/src/pages/Recon.tsx
 * - apps/web-admin/src/pages/Reports.tsx
 * - apps/web-admin/src/pages/Settings.tsx
 * - apps/web-admin/src/pages/Support.tsx
 * - apps/web-admin/src/pages/Help.tsx
 */

import type { Agent, MnoCode, OrderStatus, User } from "@evocha/shared-types";

// ──────────────────────────────────────────────
// Agent-facing user / agent (from web-agent demo.ts)
// ──────────────────────────────────────────────

export const demoUser: User = {
  id: "usr_demo",
  first_name: "Amani",
  last_name: "Mwinyi",
  locale: "en",
  role: "agent",
  mfa_enabled: false,
};

export const demoAgent: Agent = {
  id: "agt_demo",
  code: "AGT-000123",
  business_name: "Amani Mobile",
  owner_name: "Amani Mwinyi",
  tier: "standard",
  status: "active",
  kyc_status: "approved",
  region: "Dar es Salaam",
  district: "Kinondoni",
  limit_daily_minor: "50000000",
  limit_txn_minor: "5000000",
  commission_bps: 320,
};

// ──────────────────────────────────────────────
// Demo order type extension (agent-facing)
// ──────────────────────────────────────────────

export interface DemoOrder {
  ref: string;
  status: OrderStatus;
  status_label: string;
  amount_minor: string;
  amount_display: string;
  commission_minor: string;
  commission_display: string;
  fee_minor: string;
  msisdn_masked: string;
  mno: { code: MnoCode; name: string };
  provider: string;
  created_at: string;
  can_repeat: boolean;
  can_dispute: boolean;
  // Extended fields
  time: string;
  customer: string;
}

const mno = (code: MnoCode, name: string) => ({ code, name });

function order(
  ref: string,
  customer: string,
  amount: number,
  code: MnoCode,
  name: string,
  status: OrderStatus,
  time: string,
  commissionBps = 320,
): DemoOrder {
  const amountMinor = amount * 100;
  const commission = Math.round((amountMinor * commissionBps) / 10000);
  return {
    ref,
    customer,
    status,
    status_label:
      status === "SUCCESS"
        ? "Airtime sent"
        : status === "PENDING"
          ? "Request pending"
          : status === "FAILED"
            ? "Not completed"
            : "Under review",
    amount_minor: String(amountMinor),
    amount_display: "",
    commission_minor: String(commission),
    commission_display: "",
    fee_minor: "0",
    msisdn_masked: customer,
    mno: mno(code, name),
    provider: "lipawakala",
    created_at: "2026-10-23T06:15:00Z",
    can_repeat: status !== "PENDING",
    can_dispute: status === "SUCCESS",
    time,
  };
}

export const demoOrders: DemoOrder[] = [
  order("EVCH-7K2M9Q", "Juma Ally", 1000, "vodacom", "Vodacom", "SUCCESS", "09:42"),
  order("EVCH-4M8X2A", "Neema Paul", 500, "airtel", "Airtel", "PENDING", "09:38"),
  order("EVCH-2B7N5C", "Asha Salum", 2000, "tigo", "Tigo", "SUCCESS", "09:31"),
  order("EVCH-9P1L3T", "Hamisi Said", 5000, "halotel", "Halotel", "SUCCESS", "09:22"),
  order("EVCH-6H4R8D", "Rehema Joseph", 10000, "vodacom", "Vodacom", "SUCCESS", "09:14"),
  order("EVCH-1S9K6W", "Baraka Mushi", 1000, "airtel", "Airtel", "FAILED", "09:02"),
  order("EVCH-5T3D9F", "Fatuma Hassan", 2000, "tigo", "Tigo", "SUCCESS", "08:55"),
  order("EVCH-3C6V1B", "Peter Mwakalinga", 500, "vodacom", "Vodacom", "SUCCESS", "08:41"),
  order("EVCH-8N2Q4R", "Zawadi Mkinga", 5000, "halotel", "Halotel", "SUCCESS", "08:33"),
  order("EVCH-0Y7U2I", "Salma Abdallah", 1000, "airtel", "Airtel", "SUCCESS", "08:21"),
  order("EVCH-4W8E1R", "Daudi Mrema", 20000, "tigo", "Tigo", "MANUAL_REVIEW", "08:12"),
  order("EVCH-2M5N7B", "Grace Kimaro", 500, "vodacom", "Vodacom", "SUCCESS", "08:04"),
];

export const today = {
  count: 42,
  gross: "6800000",
  commission: "217600",
  success: 40,
  failed: 1,
  pending: 1,
};

export const networkMix = [
  { code: "vodacom" as MnoCode, name: "Vodacom", count: 22, gross: "3600000", colour: "#E60000" },
  { code: "airtel" as MnoCode, name: "Airtel", count: 9, gross: "1200000", colour: "#E40000" },
  { code: "tigo" as MnoCode, name: "Tigo", count: 7, gross: "1400000", colour: "#0057B8" },
  { code: "halotel" as MnoCode, name: "Halotel", count: 4, gross: "600000", colour: "#F58220" },
];

export const weekBars = [
  { day: "Mon", value: 4.2 },
  { day: "Tue", value: 5.8 },
  { day: "Wed", value: 4.9 },
  { day: "Thu", value: 7.1 },
  { day: "Fri", value: 6.4 },
  { day: "Sat", value: 8.2 },
  { day: "Sun", value: 3.6 },
];

// ──────────────────────────────────────────────
// Admin Dashboard data
// ──────────────────────────────────────────────

export interface AdminKpiCard {
  label: string;
  value: string;
  change: number;
}

export const adminKpis: AdminKpiCard[] = [
  { label: "Today's Sales (TZS)", value: "18,450,000", change: 12.4 },
  { label: "Success Rate", value: "96.8%", change: 0.8 },
  { label: "Pending Orders", value: "23", change: -4 },
  { label: "Unknown Exposure (TZS)", value: "2,340,000", change: -18.2 },
];

export interface AdminSalesData {
  day: string;
  sales: number;
  orders: number;
}

export const weeklySales: AdminSalesData[] = [
  { day: "Mon", sales: 2450000, orders: 189 },
  { day: "Tue", sales: 2890000, orders: 214 },
  { day: "Wed", sales: 2340000, orders: 178 },
  { day: "Thu", sales: 3120000, orders: 247 },
  { day: "Fri", sales: 2980000, orders: 231 },
  { day: "Sat", sales: 1890000, orders: 145 },
  { day: "Sun", sales: 1680000, orders: 128 },
];

export interface AdminNetworkMix {
  network: string;
  pct: number;
  color: string;
}

export const adminNetworkMix: AdminNetworkMix[] = [
  { network: "Vodacom", pct: 42, color: "#10B981" },
  { network: "Airtel", pct: 31, color: "#EF4444" },
  { network: "Tigo", pct: 18, color: "#2563EB" },
  { network: "Halotel", pct: 9, color: "#F59E0B" },
];

export interface AdminActivity {
  id: number;
  actor: string;
  action: string;
  target: string;
  time: string;
  type: "sale" | "kyc" | "funding" | "approval" | "alert";
}

export const activities: AdminActivity[] = [
  { id: 1, actor: "System", action: "resolved", target: "EVCH-00482 → SUCCESS", time: "2 min ago", type: "sale" },
  { id: 2, actor: "Maria Mtalo", action: "approved KYC for", target: "Agent #1247", time: "8 min ago", type: "kyc" },
  { id: 3, actor: "Finance", action: "credited funding", target: "TZS 5,000,000 — EVCH-F-391", time: "14 min ago", type: "funding" },
  { id: 4, actor: "James Mushi", action: "approved rate change", target: "Vodacom TZS 200 product", time: "23 min ago", type: "approval" },
  { id: 5, actor: "System", action: "flagged", target: "EVCH-00491 timeout (UNKNOWN)", time: "31 min ago", type: "alert" },
  { id: 6, actor: "System", action: "resolved", target: "EVCH-00478 → SUCCESS", time: "45 min ago", type: "sale" },
];

// ──────────────────────────────────────────────
// Admin Orders page
// ──────────────────────────────────────────────

export interface AdminOrder {
  ref: string;
  agent: string;
  msisdn: string;
  network: string;
  amount: string;
  fee: string;
  status: "SUCCESS" | "FAILED" | "PENDING" | "UNKNOWN";
  route: string;
  time: string;
  idempotencyKey: string;
}

export const adminOrders: AdminOrder[] = [
  { ref: "EVCH-00482", agent: "Juma Hassan", msisdn: "255712***678", network: "Vodacom", amount: "10,000", fee: "200", status: "SUCCESS", route: "LipaWakala", time: "2 min ago", idempotencyKey: "idem-001" },
  { ref: "EVCH-00491", agent: "Ashaka Mwangi", msisdn: "255654***987", network: "Airtel", amount: "5,000", fee: "100", status: "UNKNOWN", route: "LipaWakala", time: "12 min ago", idempotencyKey: "idem-002" },
  { ref: "EVCH-00488", agent: "Neema Kileo", msisdn: "255789***345", network: "Tigo", amount: "20,000", fee: "400", status: "PENDING", route: "LipaWakala", time: "18 min ago", idempotencyKey: "idem-003" },
  { ref: "EVCH-00478", agent: "Baraka Mrema", msisdn: "255611***455", network: "Vodacom", amount: "1,000", fee: "50", status: "SUCCESS", route: "LipaWakala", time: "31 min ago", idempotencyKey: "idem-004" },
  { ref: "EVCH-00475", agent: "Theresia Mturi", msisdn: "255722***344", network: "Halotel", amount: "500", fee: "0", status: "FAILED", route: "LipaWakala", time: "45 min ago", idempotencyKey: "idem-005" },
  { ref: "EVCH-00470", agent: "Juma Hassan", msisdn: "255712***678", network: "Airtel", amount: "2,500", fee: "50", status: "SUCCESS", route: "LipaWakala", time: "1 hr ago", idempotencyKey: "idem-006" },
  { ref: "EVCH-00465", agent: "Hassan Omari", msisdn: "255600***123", network: "Vodacom", amount: "15,000", fee: "300", status: "SUCCESS", route: "LipaWakala", time: "2 hr ago", idempotencyKey: "idem-007" },
  { ref: "EVCH-00460", agent: "Neema Kileo", msisdn: "255789***345", network: "Tigo", amount: "3,000", fee: "50", status: "SUCCESS", route: "LipaWakala", time: "3 hr ago", idempotencyKey: "idem-008" },
  { ref: "EVCH-00455", agent: "Baraka Mrema", msisdn: "255611***455", network: "Airtel", amount: "7,500", fee: "150", status: "FAILED", route: "LipaWakala", time: "4 hr ago", idempotencyKey: "idem-009" },
  { ref: "EVCH-00450", agent: "Ashaka Mwangi", msisdn: "255654***987", network: "Vodacom", amount: "25,000", fee: "500", status: "SUCCESS", route: "LipaWakala", time: "5 hr ago", idempotencyKey: "idem-010" },
];

// ──────────────────────────────────────────────
// Admin Agents page
// ──────────────────────────────────────────────

export interface AdminAgent {
  id: string;
  name: string;
  phone: string;
  email: string;
  tier: string;
  kycStatus: "verified" | "pending" | "suspended" | "not_submitted";
  balance: string;
  totalSales: number;
  lastLogin: string;
  status: "active" | "suspended";
}

export const agents: AdminAgent[] = [
  { id: "AGT-001", name: "Juma Hassan", phone: "255712345678", email: "juma@email.co.tz", tier: "Agent", kycStatus: "verified", balance: "1,245,000", totalSales: 847, lastLogin: "Today", status: "active" },
  { id: "AGT-002", name: "Ashaka Mwangi", phone: "255654321987", email: "ashaka@email.co.tz", tier: "Agent", kycStatus: "pending", balance: "890,000", totalSales: 234, lastLogin: "Yesterday", status: "active" },
  { id: "AGT-003", name: "Neema Kileo", phone: "255789012345", email: "neema@email.co.tz", tier: "Super Dealer", kycStatus: "verified", balance: "15,800,000", totalSales: 3421, lastLogin: "Today", status: "active" },
  { id: "AGT-004", name: "Baraka Mrema", phone: "255611223344", email: "baraka@email.co.tz", tier: "Agent", kycStatus: "verified", balance: "320,000", totalSales: 112, lastLogin: "3 days ago", status: "active" },
  { id: "AGT-005", name: "Theresia Mturi", phone: "255722334455", email: "theresia@email.co.tz", tier: "Agent", kycStatus: "suspended", balance: "0", totalSales: 56, lastLogin: "2 weeks ago", status: "suspended" },
  { id: "AGT-006", name: "Hassan Omari", phone: "255600111222", email: "hassan@email.co.tz", tier: "Dealer", kycStatus: "verified", balance: "4,500,000", totalSales: 1203, lastLogin: "Today", status: "active" },
  { id: "AGT-007", name: "Faith Mushi", phone: "255733445566", email: "faith@email.co.tz", tier: "Agent", kycStatus: "not_submitted", balance: "0", totalSales: 0, lastLogin: "Never", status: "active" },
];

// ──────────────────────────────────────────────
// Admin Wallets page
// ──────────────────────────────────────────────

export interface AdminWallet {
  id: string;
  agent: string;
  phone: string;
  available: string;
  reserved: string;
  commission: string;
  status: "active" | "blocked";
  lastActivity: string;
}

export const wallets: AdminWallet[] = [
  { id: "WLT-001", agent: "Juma Hassan", phone: "255712***678", available: "1,245,000", reserved: "10,000", commission: "32,500", status: "active", lastActivity: "2 min ago" },
  { id: "WLT-002", agent: "Neema Kileo", phone: "255789***345", available: "15,800,000", reserved: "20,000", commission: "450,000", status: "active", lastActivity: "5 min ago" },
  { id: "WLT-003", agent: "Baraka Mrema", phone: "255611***455", available: "320,000", reserved: "5,000", commission: "8,200", status: "active", lastActivity: "1 hr ago" },
  { id: "WLT-004", agent: "Theresia Mturi", phone: "255722***344", available: "0", reserved: "0", commission: "1,200", status: "blocked", lastActivity: "2 weeks ago" },
  { id: "WLT-005", agent: "Hassan Omari", phone: "255600***123", available: "4,500,000", reserved: "15,000", commission: "125,000", status: "active", lastActivity: "10 min ago" },
];

// ──────────────────────────────────────────────
// Admin KYC page
// ──────────────────────────────────────────────

export interface AdminKycCase {
  id: string;
  agent: string;
  email: string;
  phone: string;
  tier: string;
  submitted: string;
  status: "pending" | "approved" | "rejected";
  documents: string[];
  notes?: string;
}

export const kycCases: AdminKycCase[] = [
  {
    id: "KYC-001",
    agent: "Juma Hassan",
    email: "juma.hassan@email.co.tz",
    phone: "255712345678",
    tier: "Agent",
    submitted: "2026-10-22 14:30",
    status: "pending",
    documents: ["NIDA Front", "NIDA Back", "Selfie with ID"],
  },
  {
    id: "KYC-002",
    agent: "Ashaka Mwangi",
    email: "ashaka.m@email.co.tz",
    phone: "255654321987",
    tier: "Agent",
    submitted: "2026-10-22 11:15",
    status: "pending",
    documents: ["Passport", "Passport"],
  },
  {
    id: "KYC-003",
    agent: "Neema Kileo",
    email: "neema.k@email.co.tz",
    phone: "255789012345",
    tier: "Super Dealer",
    submitted: "2026-10-21 09:45",
    status: "pending",
    documents: ["NIDA Front", "NIDA Back", "Business License"],
    notes: "High-volume dealer, requires maker-checker approval for tier.",
  },
  {
    id: "KYC-004",
    agent: "Baraka Mrema",
    email: "baraka.m@email.co.tz",
    phone: "255611223344",
    tier: "Agent",
    submitted: "2026-10-21 08:00",
    status: "approved",
    documents: ["NIDA Front", "NIDA Back"],
  },
  {
    id: "KYC-005",
    agent: "Theresia Mturi",
    email: "theresia.m@email.co.tz",
    phone: "255722334455",
    tier: "Agent",
    submitted: "2026-10-20 16:20",
    status: "rejected",
    documents: ["NIDA Front", "NIDA Back"],
    notes: "Document expired. Agent notified to resubmit.",
  },
];

// ──────────────────────────────────────────────
// Admin Funding page
// ──────────────────────────────────────────────

export interface AdminFundingOrder {
  ref: string;
  agent: string;
  amount: string;
  method: string;
  status: "credited" | "pending" | "unmatched" | "failed";
  time: string;
}

export const fundings: AdminFundingOrder[] = [
  { ref: "EVCH-F-391", agent: "Neema Kileo", amount: "5,000,000", method: "Bank Transfer", status: "credited", time: "14 min ago" },
  { ref: "EVCH-F-390", agent: "Juma Hassan", amount: "2,000,000", method: "LipaWakala", status: "credited", time: "32 min ago" },
  { ref: "EVCH-F-389", agent: "Hassan Omari", amount: "10,000,000", method: "Bank Transfer", status: "pending", time: "1 hr ago" },
  { ref: "EVCH-F-388", agent: "Baraka Mrema", amount: "500,000", method: "M-Pesa", status: "credited", time: "2 hr ago" },
  { ref: "EVCH-F-387", agent: "Unknown", amount: "1,500,000", method: "Unknown", status: "unmatched", time: "3 hr ago" },
  { ref: "EVCH-F-386", agent: "Ashaka Mwangi", amount: "3,000,000", method: "LipaWakala", status: "failed", time: "5 hr ago" },
];

// ──────────────────────────────────────────────
// Admin Approvals page
// ──────────────────────────────────────────────

export interface AdminApproval {
  id: string;
  type: "kyc" | "rate" | "route" | "adjustment" | "refund" | "role";
  title: string;
  description: string;
  requestedBy: string;
  requestedAt: string;
  priority: "normal" | "high" | "urgent";
}

export const approvals: AdminApproval[] = [
  { id: "APR-001", type: "kyc", title: "KYC Approval", description: "Neema Kileo — Super Dealer tier, KYC-003", requestedBy: "Maria Mtalo", requestedAt: "5 min ago", priority: "high" },
  { id: "APR-002", type: "rate", title: "Rate Change", description: "Vodacom TZS 200 product margin +0.5%", requestedBy: "James Mushi", requestedAt: "23 min ago", priority: "normal" },
  { id: "APR-003", type: "adjustment", title: "Wallet Adjustment", description: "Agent AGT-001 — credit TZS 50,000 (customer dispute)", requestedBy: "Maria Mtalo", requestedAt: "1 hr ago", priority: "urgent" },
  { id: "APR-004", type: "refund", title: "Refund Approval", description: "EVCH-00475 — failed sale refund TZS 500", requestedBy: "James Mushi", requestedAt: "2 hr ago", priority: "normal" },
  { id: "APR-005", type: "route", title: "Route Toggle", description: "Enable Selcom as secondary route for Airtel", requestedBy: "James Mushi", requestedAt: "3 hr ago", priority: "normal" },
];

// ──────────────────────────────────────────────
// Admin Finance page
// ──────────────────────────────────────────────

export interface DailyFinanceData {
  date: string;
  gmv: string;
  orders: number;
  commission: string;
  margin: string;
  providers: string;
}

export const dailyFinanceData: DailyFinanceData[] = [
  { date: "2026-10-22", gmv: "45,230,000", orders: 342, commission: "678,000", margin: "2.4%", providers: "LipaWakala" },
  { date: "2026-10-21", gmv: "38,910,000", orders: 289, commission: "584,000", margin: "2.2%", providers: "LipaWakala" },
  { date: "2026-10-20", gmv: "42,150,000", orders: 312, commission: "623,000", margin: "2.3%", providers: "LipaWakala" },
  { date: "2026-10-19", gmv: "35,670,000", orders: 267, commission: "535,000", margin: "2.1%", providers: "LipaWakala" },
  { date: "2026-10-18", gmv: "48,900,000", orders: 378, commission: "733,000", margin: "2.6%", providers: "LipaWakala" },
];

// ──────────────────────────────────────────────
// Admin Refunds page
// ──────────────────────────────────────────────

export interface RefundCase {
  id: string;
  order: string;
  agent: string;
  amount: string;
  reason: string;
  status: "open" | "approved" | "rejected" | "resolved";
  time: string;
}

export const refundCases: RefundCase[] = [
  { id: "REF-001", order: "EVCH-00475", agent: "Theresia Mturi", amount: "500", reason: "Airtime not received - timeout", status: "open", time: "1 hr ago" },
  { id: "REF-002", order: "EVCH-00455", agent: "Baraka Mrema", amount: "7,500", reason: "Wrong network sent", status: "open", time: "3 hr ago" },
  { id: "REF-003", order: "EVCH-00440", agent: "Juma Hassan", amount: "10,000", reason: "Duplicate charge", status: "approved", time: "5 hr ago" },
  { id: "REF-004", order: "EVCH-00420", agent: "Ashaka Mwangi", amount: "2,000", reason: "Agent dispute - service issue", status: "rejected", time: "1 day ago" },
];

// ──────────────────────────────────────────────
// Admin Recon page
// ──────────────────────────────────────────────

export interface ReconItem {
  id: string;
  internalRef: string;
  providerRef: string;
  amount: string;
  status: "matched" | "exception" | "unmatched_internal" | "unmatched_provider";
  type: "sale" | "funding" | "refund";
  time: string;
}

export const reconItems: ReconItem[] = [
  { id: "R-001", internalRef: "EVCH-00482", providerRef: "LW-998877", amount: "10,000", status: "matched", type: "sale", time: "2 min ago" },
  { id: "R-002", internalRef: "EVCH-00491", providerRef: "LW-998866", amount: "5,000", status: "exception", type: "sale", time: "12 min ago" },
  { id: "R-003", internalRef: "EVCH-00488", providerRef: "", amount: "20,000", status: "unmatched_internal", type: "sale", time: "18 min ago" },
  { id: "R-004", internalRef: "", providerRef: "LW-998900", amount: "3,000", status: "unmatched_provider", type: "sale", time: "25 min ago" },
  { id: "R-005", internalRef: "EVCH-00478", providerRef: "LW-998855", amount: "1,000", status: "matched", type: "sale", time: "45 min ago" },
  { id: "R-006", internalRef: "EVCH-F-391", providerRef: "", amount: "5,000,000", status: "unmatched_provider", type: "funding", time: "1 hr ago" },
];

// ──────────────────────────────────────────────
// Admin Reports page
// ──────────────────────────────────────────────

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon?: React.ReactNode;
}

export const reportTemplates: ReportTemplate[] = [
  { id: "R001", name: "Daily Sales Summary", description: "GMV, orders, success rate, network breakdown by day.", category: "Sales" },
  { id: "R002", name: "Agent Statement", description: "Opening/closing balances, transactions, commission per agent.", category: "Agent" },
  { id: "R003", name: "Provider Reconciliation", description: "Platform vs provider totals, exceptions, unmatched.", category: "Reconciliation" },
  { id: "R004", name: "Pending Exposure Report", description: "All orders in PENDING/UNKNOWN state with age and value.", category: "Risk" },
  { id: "R005", name: "Commission Report", description: "Commission per agent, per network, per day.", category: "Finance" },
  { id: "R006", name: "Funding Report", description: "All funding attempts, credited amounts, unmatched receipts.", category: "Finance" },
  { id: "R007", name: "Wallet Control Report", description: "Total available + reserved + commission across all agents.", category: "Finance" },
  { id: "R008", name: "Audit Trail Export", description: "All privileged actions with actor, timestamp, and trace ID.", category: "Security" },
];

// ──────────────────────────────────────────────
// Admin Settings page
// ──────────────────────────────────────────────

export interface SettingItem {
  label: string;
  description: string;
  defaultOn: boolean;
}

export interface SettingGroup {
  title: string;
  items: SettingItem[];
}

export const settingGroups: SettingGroup[] = [
  {
    title: "Limits",
    items: [
      { label: "Min daily sale amount", description: "Minimum TZS value an agent can sell per day", defaultOn: false },
      { label: "Max single transaction", description: "Maximum TZS per airtime order", defaultOn: false },
      { label: "Max daily exposure", description: "Alert threshold for UNKNOWN orders", defaultOn: false },
    ],
  },
  {
    title: "Security",
    items: [
      { label: "Enforce MFA for admins", description: "All admin users must complete multi-factor authentication", defaultOn: true },
      { label: "Lock session on inactivity", description: "Auto-logout after 30 minutes of inactivity", defaultOn: true },
      { label: "Device binding", description: "Bind sessions to registered devices", defaultOn: true },
    ],
  },
  {
    title: "Notifications",
    items: [
      { label: "Alert on UNKNOWN orders", description: "Notify ops team when orders enter STATUS_ENQUIRY", defaultOn: true },
      { label: "Daily reconciliation report", description: "Auto-generate end-of-day reconciliation summary", defaultOn: true },
      { label: "Funding mismatch email", description: "Send alerts when unmatched receipts exceed threshold", defaultOn: true },
    ],
  },
  {
    title: "Maintenance",
    items: [
      { label: "Maintenance mode", description: "Put platforms into maintenance/read-only mode", defaultOn: false },
      { label: "Force version update", description: "Require all clients to update to latest version", defaultOn: false },
    ],
  },
];

// ──────────────────────────────────────────────
// Admin Support page
// ──────────────────────────────────────────────

export interface SupportTicket {
  id: string;
  agent: string;
  subject: string;
  status: "open" | "investigating" | "resolved";
  time: string;
}

export const supportTickets: SupportTicket[] = [
  { id: "SUP-001", agent: "Juma Hassan", subject: "Airtime not received - EVCH-00491", status: "investigating", time: "1 hr ago" },
  { id: "SUP-002", agent: "Neema Kileo", subject: "Cannot top up wallet - payment failed", status: "open", time: "3 hr ago" },
  { id: "SUP-003", agent: "Baraka Mrema", subject: "Request rate adjustment for Vodacom", status: "resolved", time: "1 day ago" },
];

// ──────────────────────────────────────────────
// Admin Help page
// ──────────────────────────────────────────────

export interface FAQ {
  q: string;
  a: string;
}

export const faqs: FAQ[] = [
  { q: "How do I approve a KYC case?", a: "Navigate to KYC Queue, click Review on a pending case, verify the uploaded documents, and click Approve or Request Info." },
  { q: "How do I resolve an UNKNOWN order?", a: "Order status UNKNOWN triggers the status enquiry worker automatically. You can manually trigger another enquiry via the Orders page detail sidebar." },
  { q: "What is maker-checker?", a: "Maker-checker requires two different users to approve sensitive actions — KYC, rate changes, wallet adjustments, refunds, and privileged role changes." },
  { q: "How do I export a daily reconciliation report?", a: "Go to Reports > Provider Reconciliation and click CSV or PDF export. The report includes matched items, exceptions, and unmatched entries." },
  { q: "Who do I contact for LipaWakala issues?", a: "Contact LipaWakala support at support@lipwakala.co.tz. For platform-level issues, contact SKT ops at ops@skttanzania.co.tz." },
];
